import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Login from '@/pages/Login';
import KnotLibrary from '@/pages/KnotLibrary';
import { api } from '@/lib/api';

const KnotEditor = lazy(() => import('@/pages/KnotEditor'));
const DevKnotSM = import.meta.env.DEV ? lazy(() => import('@/pages/DevKnotSM')) : null;

type AuthState = 'unknown' | 'authed' | 'anon';

function RequireAuth({ authed, children }: { authed: AuthState; children: JSX.Element }) {
  const location = useLocation();
  if (authed === 'unknown') {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }
  if (authed === 'anon') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

export default function App() {
  const [authed, setAuthed] = useState<AuthState>('unknown');

  useEffect(() => {
    api
      .me()
      .then((r) => setAuthed(r.authed ? 'authed' : 'anon'))
      .catch(() => setAuthed('anon'));
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login onAuthed={() => setAuthed('authed')} />} />
        <Route
          path="/knots"
          element={
            <RequireAuth authed={authed}>
              <KnotLibrary onSignedOut={() => setAuthed('anon')} />
            </RequireAuth>
          }
        />
        <Route
          path="/knots/:id/edit"
          element={
            <RequireAuth authed={authed}>
              <Suspense fallback={<div className="p-8 text-sm">Loading editor…</div>}>
                <KnotEditor />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route path="/" element={<Navigate to="/knots" replace />} />
        {DevKnotSM ? (
          <Route
            path="/dev/knot-sm"
            element={
              <Suspense fallback={<div className="p-8 text-sm">Loading dev page…</div>}>
                <DevKnotSM />
              </Suspense>
            }
          />
        ) : null}
        <Route path="*" element={<Navigate to="/knots" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
